import { useSyncExternalStore } from 'react'

/**
 * Theme systems. A theme is a full set of colour tokens written onto <html> as
 * CSS variables, so anything in app.css that reads var(--bg) etc. follows it.
 * The built-in Dark and Light write nothing and keep the stylesheet's own
 * values (including the phone's iOS greys). Custom themes live in this browser.
 */

export const TOKEN_GROUPS = [
  { title: 'Surfaces', keys: [['bg', 'Background'], ['panel', 'Panel'], ['raise', 'Raised'], ['line', 'Line'], ['lineStrong', 'Strong line'], ['sel', 'Selection'], ['hover', 'Hover']] },
  { title: 'Text', keys: [['t1', 'Primary'], ['t2', 'Secondary'], ['t3', 'Tertiary'], ['t4', 'Muted'], ['t5', 'Faint'], ['body', 'Email body']] },
  { title: 'Accent', keys: [['acc', 'Accent'], ['accText', 'Accent text'], ['warn', 'Urgent / warning'], ['ok', 'Success']] },
  { title: 'Reason tags', keys: [['tagUrgent', 'urgent'], ['tagPayment', 'payment due'], ['tagDeadline', 'deadline'], ['tagApproval', 'needs OK'], ['tagInvite', 'invite'], ['tagQuestion', 'asked you'], ['tagWaiting', 'waiting on you'], ['tagTodo', 'to do']] },
] as const

export type TokenKey = (typeof TOKEN_GROUPS)[number]['keys'][number][0]
export type Tokens = Record<TokenKey, string>
export interface Theme { id: string; name: string; mode: 'dark' | 'light'; tokens: Tokens; custom?: boolean }

const CSS_VAR: Record<TokenKey, string> = {
  bg: '--bg', panel: '--panel', raise: '--raise', line: '--line', lineStrong: '--line-strong', sel: '--sel', hover: '--hover',
  t1: '--t1', t2: '--t2', t3: '--t3', t4: '--t4', t5: '--t5', body: '--body',
  acc: '--acc', accText: '--acc-text', warn: '--warn', ok: '--ok',
  tagUrgent: '--tag-urgent', tagPayment: '--tag-payment', tagDeadline: '--tag-deadline', tagApproval: '--tag-approval',
  tagInvite: '--tag-invite', tagQuestion: '--tag-question', tagWaiting: '--tag-waiting', tagTodo: '--tag-todo',
}

/** Which tag colour a reason uses (rows and the thread header carry data-tag). */
export const TAG_FOR_REASON: Record<string, string> = {
  urgent: 'urgent', 'payment due': 'payment', deadline: 'deadline', 'needs OK': 'approval', invite: 'invite',
  'asked you': 'question', 'waiting on you': 'waiting', 'to do': 'todo',
}

// An empty tag colour means "automatic": grey in lists, the accent in the thread header, urgent in the warning colour.
const plainTags = (..._unused: string[]): Pick<Tokens, 'tagUrgent' | 'tagPayment' | 'tagDeadline' | 'tagApproval' | 'tagInvite' | 'tagQuestion' | 'tagWaiting' | 'tagTodo'> =>
  ({ tagUrgent: '', tagPayment: '', tagDeadline: '', tagApproval: '', tagInvite: '', tagQuestion: '', tagWaiting: '', tagTodo: '' })

const DARK: Tokens = {
  bg: '#0b0b0b', panel: '#111111', raise: '#161616', line: '#1c1c1c', lineStrong: '#2a2a2a', sel: 'rgba(10, 132, 255, 0.14)', hover: 'rgba(255, 255, 255, 0.035)',
  t1: '#f2f2f2', t2: '#bdbdbd', t3: '#8a8a8a', t4: '#6e6e6e', t5: '#4a4a4a', body: '#ededed',
  acc: '#0a84ff', accText: '#4da3ff', warn: '#ff9f6e', ok: '#63d38d', ...plainTags('#8a8a8a', '#ff9f6e'),
}
const LIGHT: Tokens = {
  bg: '#ffffff', panel: '#fafafa', raise: '#f4f4f4', line: '#ececec', lineStrong: '#dddddd', sel: 'rgba(0, 122, 255, 0.08)', hover: 'rgba(0, 0, 0, 0.025)',
  t1: '#111111', t2: '#3c3c3c', t3: '#6e6e6e', t4: '#8a8a8a', t5: '#b5b5b5', body: '#1a1a1a',
  acc: '#007aff', accText: '#0066d6', warn: '#c2410c', ok: '#15803d', ...plainTags('#6e6e6e', '#c2410c'),
}

export const BUILT_IN: Theme[] = [
  { id: 'dark', name: 'Dark', mode: 'dark', tokens: DARK },
  { id: 'light', name: 'Light', mode: 'light', tokens: LIGHT },
  { id: 'inky', name: 'Inky', mode: 'dark', tokens: {
    ...DARK, bg: '#000000', panel: '#0d0d0d', raise: '#161616', line: '#161616', lineStrong: '#242424', sel: 'rgba(61, 139, 255, 0.16)',
    t1: '#f5f5f5', t2: '#c2c2c2', t3: '#8e8e8e', t4: '#6b6b6b', t5: '#444444', acc: '#3d8bff', accText: '#6aa6ff', warn: '#ff8a5c', ...plainTags('#8e8e8e', '#ff8a5c') } },
  { id: 'overcast', name: 'Overcast', mode: 'dark', tokens: {
    ...DARK, bg: '#1c1c1e', panel: '#242426', raise: '#2e2e31', line: '#2c2c2f', lineStrong: '#3a3a3e', sel: 'rgba(10, 132, 255, 0.20)', hover: 'rgba(255, 255, 255, 0.04)',
    t1: '#ececee', t2: '#c4c4c8', t3: '#9a9aa0', t4: '#7c7c82', t5: '#5a5a60', body: '#e6e6e8', acc: '#0a84ff', accText: '#64a8ff', ...plainTags('#9a9aa0', '#ff9f6e') } },
  { id: 'monokai', name: 'Overcast Monokai', mode: 'dark', tokens: {
    bg: '#2d2a2e', panel: '#353236', raise: '#403e41', line: '#403e41', lineStrong: '#4d4a4e', sel: 'rgba(120, 220, 232, 0.13)', hover: 'rgba(252, 252, 250, 0.04)',
    t1: '#fcfcfa', t2: '#c1c0c0', t3: '#939293', t4: '#727072', t5: '#5b595c', body: '#f2f1ef',
    acc: '#78dce8', accText: '#78dce8', warn: '#ff6188', ok: '#a9dc76',
    tagUrgent: '#ff6188', tagPayment: '#ffd866', tagDeadline: '#fc9867', tagApproval: '#a9dc76', tagInvite: '#ab9df2', tagQuestion: '#78dce8', tagWaiting: '#78dce8', tagTodo: '#fc9867' } },
  { id: 'candlelit', name: 'Candlelit', mode: 'dark', tokens: {
    ...DARK, bg: '#14110e', panel: '#1c1814', raise: '#26201a', line: '#241e18', lineStrong: '#33291f', sel: 'rgba(233, 165, 91, 0.14)', hover: 'rgba(239, 230, 218, 0.035)',
    t1: '#efe6da', t2: '#cdbfae', t3: '#9c8e7e', t4: '#7a6e61', t5: '#544b42', body: '#ebe2d6', acc: '#e9a55b', accText: '#f0b571', warn: '#f07a5a', ...plainTags('#9c8e7e', '#f07a5a') } },
  { id: 'dashboard', name: 'Dashboard', mode: 'dark', tokens: {
    ...DARK, bg: '#101214', panel: '#16191c', raise: '#1e2226', line: '#1f2327', lineStrong: '#2b3036', sel: 'rgba(230, 233, 236, 0.07)', hover: 'rgba(230, 233, 236, 0.03)',
    t1: '#e6e9ec', t2: '#bcc2c8', t3: '#8b9299', t4: '#6c737a', t5: '#4a5056', body: '#e2e5e8', acc: '#e6e9ec', accText: '#e6e9ec', warn: '#ffb020', ...plainTags('#8b9299', '#ffb020') } },
]

// ---- Custom themes (this browser) ----
const KEY = 'mm.themes'
const listeners = new Set<() => void>()
let customs: Theme[] = (() => { try { return (JSON.parse(localStorage.getItem(KEY) || '[]') as Theme[]).map(theme => ({ ...theme, custom: true })) } catch { return [] } })()
let snapshot = [...BUILT_IN, ...customs]
const save = () => { localStorage.setItem(KEY, JSON.stringify(customs.map(({ custom: _, ...theme }) => theme))); snapshot = [...BUILT_IN, ...customs]; for (const listener of listeners) listener() }

export const useThemes = () => useSyncExternalStore(listener => { listeners.add(listener); return () => listeners.delete(listener) }, () => snapshot)
export const themeList = () => snapshot
export const themeById = (id: string) => snapshot.find(theme => theme.id === id)

export function createTheme(from: Theme, name = `${from.name} copy`): Theme {
  const theme: Theme = { id: `custom-${crypto.randomUUID().slice(0, 8)}`, name, mode: from.mode, tokens: { ...from.tokens }, custom: true }
  customs = [...customs, theme]; save()
  return theme
}
export function updateTheme(id: string, change: Partial<Pick<Theme, 'name' | 'mode'>> & { tokens?: Partial<Tokens> }) {
  customs = customs.map(theme => theme.id === id ? { ...theme, ...change, tokens: { ...theme.tokens, ...change.tokens } } : theme); save()
}
export function deleteTheme(id: string) { customs = customs.filter(theme => theme.id !== id); save() }

/** Paste a theme someone shared (or edited by hand). Missing tokens are filled from its mode's default. */
export function importTheme(json: string): Theme {
  const raw = JSON.parse(json) as Partial<Theme> & { tokens?: Partial<Tokens> }
  const mode = raw.mode === 'light' ? 'light' : 'dark'
  const base = mode === 'light' ? LIGHT : DARK
  const tokens = { ...base } as Tokens
  for (const key of Object.keys(base) as TokenKey[]) {
    const value = raw.tokens?.[key]
    if (typeof value === 'string' && (value === '' || CSS.supports('color', value))) tokens[key] = value
  }
  const theme: Theme = { id: `custom-${crypto.randomUUID().slice(0, 8)}`, name: String(raw.name || 'Imported theme').slice(0, 40), mode, tokens, custom: true }
  customs = [...customs, theme]; save()
  return theme
}
export const exportTheme = (theme: Theme) => JSON.stringify({ name: theme.name, mode: theme.mode, tokens: theme.tokens }, null, 2)

/** Write a theme onto the page. Built-in Dark/Light clear the overrides so the stylesheet decides. */
export function paintTheme(theme: Theme | undefined, mode: 'dark' | 'light') {
  const root = document.documentElement
  root.dataset.theme = theme?.mode ?? mode
  for (const name of Object.values(CSS_VAR)) root.style.removeProperty(name)
  if (!theme || theme.id === 'dark' || theme.id === 'light') return
  for (const [key, name] of Object.entries(CSS_VAR) as Array<[TokenKey, string]>) if (theme.tokens[key]) root.style.setProperty(name, theme.tokens[key])
}
